import { EnumUserRoles } from 'entities/Users/model/types'
import { type NavArea } from 'entities/WebMcp/model/navAreas'
import { roleAtLeast } from 'entities/WebMcp/model/roleGating'
import { areaStarters, onboardingStarters } from 'entities/WebMcp/model/starters'
import {
  type OnboardingStatus,
  type OnboardingStepKey,
} from 'entities/WebMcp/lib/onboardingSignals'
import { capabilityLine } from 'features/AiAgent/model/roleContext'

export interface EmptyStatePill {
  label: string
  prompt: string
}

export type EmptyStateTier = 'onboarding' | 'page' | 'discovery' | 'none'

export interface EmptyStateSelection {
  line: string
  tier: EmptyStateTier
  intro: string | null
  pills: EmptyStatePill[]
}

const MAX_PILLS = 3

// The empty-state onboarding nudge is kept light — at most the next couple of
// steps. The full remaining list lives in the command palette's "Finish setup"
// group, reached via the "see all setup steps" affordance.
const ONBOARDING_NUDGE_MAX = 2

// Sending a message or making a call are the core actions that mean the user has
// actually started using the product (neither can be auto-seeded). Once either is
// done the user is "activated": the onboarding nudge steps aside from the empty
// state (remaining steps stay available in the command palette).
const ACTIVATION_KEYS: ReadonlyArray<OnboardingStepKey> = ['first_message', 'first_call']

const isActivated = (onboarding: OnboardingStatus): boolean =>
  onboarding.steps.some((s) => ACTIVATION_KEYS.includes(s.key) && s.status === 'complete')

const toPill = ({ label, prompt }: { label: string; prompt: string }): EmptyStatePill => ({
  label,
  prompt,
})

/**
 * Picks the agent panel's empty-state content: a role-aware description line plus
 * a tier — onboarding next-steps → current-page actions → a "browse the command
 * menu" nudge. The discovery tier carries no pills (the caller renders a nudge
 * that opens the command menu, the comprehensive discovery surface) rather than
 * arbitrary cross-area pills that do not fit the current screen. Pure; the caller
 * supplies the resolved area, onboarding status, and role. `onboarding: null`
 * (not yet loaded) simply skips the onboarding tier, as does `onboardingSkipped`
 * (the user chose "Skip for now") — in both cases the empty state advances to the
 * page / discovery tiers while the steps remain in the command palette.
 */
export const selectEmptyState = ({
  area,
  onboarding,
  role,
  onboardingSkipped = false,
}: {
  area: NavArea | null
  onboarding: OnboardingStatus | null
  role?: EnumUserRoles
  /** The user dismissed the onboarding nudge with "Skip for now" (local state). */
  onboardingSkipped?: boolean
}): EmptyStateSelection => {
  const line = capabilityLine(role)

  if (onboarding && role && !onboardingSkipped) {
    // Pre-activation only: nudge toward the first couple of steps (texting or
    // calling). Once activated, onboarding steps aside — the empty state advances
    // and the remaining steps live in the command palette's "Finish setup" group.
    // No "N steps left" count here (the palette carries the full list).
    const pills = onboardingStarters(onboarding, role).map(toPill)
    if (pills.length && !isActivated(onboarding)) {
      return {
        line,
        tier: 'onboarding',
        intro: "Let's get you started:",
        pills: pills.slice(0, ONBOARDING_NUDGE_MAX),
      }
    }
  }

  // Page and discovery tiers offer write-adjacent actions, so require at least a
  // member (observer / unknown role get the description line only).
  if (role && roleAtLeast(role, EnumUserRoles.MEMBER)) {
    const page = areaStarters(area, role).map(toPill)
    if (page.length)
      return {
        line,
        tier: 'page',
        intro: 'From this screen you might want to:',
        pills: page.slice(0, MAX_PILLS),
      }

    // No page-specific actions: nudge to the command menu instead of arbitrary
    // cross-area pills. The caller supplies this tier's two openers (browse the
    // menu, ask about a feature), so the line ends in a colon like the others.
    return {
      line,
      tier: 'discovery',
      intro: 'If you are not sure where to start, try:',
      pills: [],
    }
  }

  return { line, tier: 'none', intro: null, pills: [] }
}
